import {MithrilComponent} from '@bitstillery/mithril'
import {Button, TippyJsx} from '@bitstillery/common/components'
import {state} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'

import type {CollectionProxy, CollectionRow} from '@bitstillery/common/lib/collection'
import type {ValidationData} from '@bitstillery/common/lib/validation'

/** A row an action works on (a collection row, a bulk row); the action flags it as loading while it runs. */
type ActionRow = object & {loading?: boolean}

interface RowActionDetailsAttrs {
    /** CollectionProxy to adjust detail state for */
    collection: CollectionProxy
    /** A collection item */
    row: ActionRow & CollectionRow
}

export class RowActionDetails extends MithrilComponent<RowActionDetailsAttrs> {
    view(vnode: m.Vnode<RowActionDetailsAttrs>) {
        const detail = vnode.attrs.collection.state.detail
        const is_active = detail.artkey === vnode.attrs.row.artkey
        const row_key = `${vnode.attrs.collection._id}-${vnode.attrs.row.artkey}`
        return (
            <Button
                active={is_active}
                disabled={vnode.attrs.row.loading}
                icon='info'
                onclick={() => {
                    if (!is_active) {
                        vnode.attrs.collection.state.detail.artkey = vnode.attrs.row.artkey
                        vnode.attrs.collection.state.detail.key = row_key
                    } else {
                        vnode.attrs.collection.state.detail.artkey = null
                        vnode.attrs.collection.state.detail.key = ''
                    }
                }}
                tip={() => (is_active ? 'Hide item details' : 'Show item details')}
                type='info'
                variant='toggle'
            />
        )
    }
}

export interface RowActionDeleteAttrs {
    disabled?: boolean
    /** An optional icon override (default is 'trash') */
    icon?: string
    /** Whether to require an additional click for high-risk actions */
    needs_confirmation?: boolean
    /** Size of the button */
    size?: 'xs' | 's' | 'd' | 'l' | 'xl'
    /** A collection item */
    row: ActionRow
    /** A save_row method to call when clicking this save button */
    row_delete: () => unknown
    /** A tabindex override */
    tabindex?: number
    // An optional tip override
    tip?: string | (() => string)
    // An optional button type override (default is 'success')
    type?: 'brand' | 'danger' | 'surface' | 'default' | 'info' | 'success' | 'warning' | 'unset' | (string & {})
    /** The $v validation object that determines if the related fields have been validated before trying to save*/
    validation?: Partial<ValidationData>
    /** Deactivate flavour of a delete action */
    variant?: 'deactivate'
}

export class RowActionDelete extends MithrilComponent<RowActionDeleteAttrs> {
    data = state({
        confirmed: false,
    })

    view(vnode: m.Vnode<RowActionDeleteAttrs>) {
        let icon: string, tip: string | undefined, tip_confirm: string | undefined
        if (vnode.attrs.icon) {
            icon = vnode.attrs.icon
        } else if (vnode.attrs.variant === 'deactivate') {
            icon = 'deactivate'
            tip = 'Deactivate this item'
            tip_confirm = 'I am sure; delete this item'
        } else {
            icon = 'trash'
            tip = 'Delete this item'
            tip_confirm = 'I am sure; deactivate this item'
        }
        if (this.data.confirmed) {
            return [
                <Button
                    icon={icon}
                    onclick={async (e) => {
                        e.stopPropagation()
                        vnode.attrs.row.loading = true
                        await vnode.attrs.row_delete()
                        vnode.attrs.row.loading = false
                    }}
                    tip={tip_confirm}
                    size={vnode.attrs.size ? vnode.attrs.size : 'd'}
                    tabindex={vnode.attrs.tabindex}
                    type='danger'
                    variant='toggle'
                />,
                <Button
                    icon='cancel'
                    onclick={(e) => {
                        e.stopPropagation()
                        this.data.confirmed = false
                    }}
                    size={vnode.attrs.size ? vnode.attrs.size : 'd'}
                    tabindex={vnode.attrs.tabindex}
                    tip='Cancel action'
                    variant='toggle'
                />,
            ]
        }
        return (
            <Button
                disabled={vnode.attrs.row.loading || vnode.attrs.disabled}
                icon={icon}
                onclick={async (e) => {
                    e.stopPropagation()
                    vnode.attrs.row.loading = true
                    if (vnode.attrs.needs_confirmation) {
                        this.data.confirmed = true
                        setTimeout(() => {
                            this.data.confirmed = false
                        }, 1500)
                    } else {
                        vnode.attrs.row.loading = true
                        await vnode.attrs.row_delete()
                        vnode.attrs.row.loading = false
                    }
                    vnode.attrs.row.loading = false
                }}
                tabindex={vnode.attrs.tabindex}
                tip={() => {
                    if (vnode.attrs.tip) {
                        if (typeof vnode.attrs.tip === 'function') {
                            return vnode.attrs.tip()
                        }
                        return vnode.attrs.tip
                    }

                    if (vnode.attrs.needs_confirmation && tip) {
                        return `${tip} (needs confirmation)`
                    } else {
                        return tip
                    }
                }}
                size={vnode.attrs.size ? vnode.attrs.size : 'd'}
                type={vnode.attrs.type ? vnode.attrs.type : 'danger'}
                variant='toggle'
            />
        )
    }
}

interface RowActionDownloadAttrs {
    disabled?: boolean
    icon?: string
    mode?: string
    row: ActionRow
    row_download: () => unknown
    tip?: string
    type?: 'default' | 'info' | 'success' | 'warning' | 'danger' | (string & {})
}
export class RowActionDownload extends MithrilComponent<RowActionDownloadAttrs> {
    view(vnode: m.Vnode<RowActionDownloadAttrs>) {
        return (
            <Button
                disabled={vnode.attrs.disabled}
                icon={vnode.attrs.icon ? vnode.attrs.icon : 'download'}
                onclick={async (e) => {
                    e.stopPropagation()
                    vnode.attrs.row.loading = true
                    await vnode.attrs.row_download()
                    vnode.attrs.row.loading = false
                }}
                tip={() => {
                    if (vnode.attrs.tip) {
                        return vnode.attrs.tip
                    }

                    return 'Download this item'
                }}
                type={vnode.attrs.type ? vnode.attrs.type : 'info'}
                variant='toggle'
            />
        )
    }
}

interface RowActionProcessedAttrs<R extends ActionRow> {
    /** A collection item */
    row: R
    /** The row to mark as processed; it is removed afterwardes */
    row_process: (row: R) => unknown
    tip?: string
    type?: 'brand' | 'danger' | 'surface' | 'default' | 'info' | 'success' | 'warning' | 'unset' | (string & {})
}

export class RowActionProcessed<R extends ActionRow = ActionRow> extends MithrilComponent<RowActionProcessedAttrs<R>> {
    view(vnode: m.Vnode<RowActionProcessedAttrs<R>>) {
        return (
            <Button
                disabled={vnode.attrs.row.loading}
                icon='checked'
                onclick={(e) => {
                    // Change row logic in the onclick.
                    vnode.attrs.row_process(vnode.attrs.row)

                    e.stopPropagation()
                }}
                tip={() =>
                    vnode.attrs.tip ? vnode.attrs.tip : "Mark this item as processed, so it doesn't show up in this list again."
                }
                type={vnode.attrs.type ? vnode.attrs.type : 'info'}
                variant='toggle'
            />
        )
    }
}

interface RowActionSaveAttrs {
    // An optional icon override (default is 'save)
    icon?: 'string'
    /** A collection item */
    row: ActionRow
    /** A save_row method to call when clicking this save button */
    row_save: () => unknown
    // An optional tip override
    tip?: string
    // An optional button type override (default is 'success')
    type?: 'brand' | 'danger' | 'surface' | 'default' | 'info' | 'success' | 'warning' | 'unset' | (string & {})
    /** The $v validation object that determines if the related fields have been validated before trying to save*/
    validation: Partial<ValidationData>
}

export class RowActionSave extends MithrilComponent<RowActionSaveAttrs> {
    view(vnode: m.Vnode<RowActionSaveAttrs>) {
        return (
            <Button
                disabled={
                    vnode.attrs.row.loading || !!Object.values(vnode.attrs.validation).filter((i) => i && i._invalid).length
                }
                icon={vnode.attrs.icon ? vnode.attrs.icon : 'save'}
                onclick={async (e) => {
                    e.stopPropagation()
                    vnode.attrs.row.loading = true
                    await vnode.attrs.row_save()
                    vnode.attrs.row.loading = false
                }}
                tip={() => (vnode.attrs.tip ? vnode.attrs.tip : 'Save this item')}
                type={vnode.attrs.type ? vnode.attrs.type : 'success'}
                variant='toggle'
            />
        )
    }
}

interface RowActionSearchProductAttrs {
    /** The product to search for; a TBO search also narrows by its specs. */
    row: {
        alcohol_percentage: string | number
        gift_box_type?: string | null
        number_of_bottles_per_case?: number | null
        product_name: string
        volume: string | number
    }
    is_tbo?: boolean
}

export class RowActionSearchProduct extends MithrilComponent<RowActionSearchProductAttrs> {
    view(vnode: m.Vnode<RowActionSearchProductAttrs>) {
        const row = vnode.attrs.row
        const compare_url = encodeURI(`/pricelist/compare-prices/list?product_search=${row.product_name}`)
        const discover_url = encodeURI(`/market/discover?search_terms=${row.product_name}`)
        const stock_url = encodeURI(`/stock/items/list/manage?search=${row.product_name}`)
        const offer_item_list_url = encodeURI(`/pricelist/sales-prices/list?search=${row.product_name}`)

        let tbo_manage_url: null | string = null
        if (vnode.attrs.is_tbo) {
            const tbo_params = [
                row.product_name,
                +row.alcohol_percentage,
                +row.volume,
                row.number_of_bottles_per_case,
                row.gift_box_type,
            ].filter((value) => !!value)

            tbo_manage_url = encodeURI(`/pricelist/tbo-approval/list/manage?search=${tbo_params.join(' ')}`)
        }
        return (
            <div className='c-row-action-search-product'>
                <TippyJsx
                    allowHTML={true}
                    appendTo={document.body}
                    content={
                        <div className='c-row-action-search-product'>
                            <div className='search-header'>Search Product in:</div>
                            <div className='search-options'>
                                <a href={discover_url} target='_blank'>
                                    🛒 Market
                                </a>
                                <a href={compare_url} target='_blank'>
                                    ⚖️ Compare
                                </a>
                                <a href={offer_item_list_url} target='_blank'>
                                    💼 Offer items
                                </a>
                                <a href={stock_url} target='_blank'>
                                    📦 Stock
                                </a>
                                {tbo_manage_url && (
                                    <a href={tbo_manage_url} target='_blank'>
                                        📝 TBO manage
                                    </a>
                                )}
                            </div>
                            <div className='search-footer'>{row.product_name}</div>
                        </div>
                    }
                    interactive={true}
                >
                    <Button icon='search' type='info' variant='toggle' />
                </TippyJsx>
            </div>
        )
    }
}
